import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { overrideOrganizationHeader } from '@cast/api-client';
import { apiClient } from '@cast/infrastructure-api';
import { ServiceAccountWithGroups } from '@cast/features-organization-shared-types';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { SortOrder } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

export const ENTERPRISE_SERVICE_ACCOUNTS_QUERY_KEY =
  'enterprise-service-accounts';

type FilterValues = {
  organizationId?: string[];
  name?: string;
};

export const getEnterpriseServiceAccountsQueryKey = (params: {
  enterpriseOrgId: string;
  pageLimit?: number;
  sortField?: string;
  sortOrder?: SortOrder;
  filters?: FilterValues;
}) => {
  if (params.pageLimit === undefined) {
    return [
      ENTERPRISE_SERVICE_ACCOUNTS_QUERY_KEY,
      params.enterpriseOrgId,
    ] as const;
  }

  return [
    ENTERPRISE_SERVICE_ACCOUNTS_QUERY_KEY,
    params.enterpriseOrgId,
    params.pageLimit,
    params.sortField,
    params.sortOrder,
    params.filters,
  ] as const;
};

type EnterpriseServiceAccountsQueryParams = {
  enterpriseOrgId: string;
  pageLimit?: number;
  sortField?: string;
  sortOrder?: SortOrder;
  filters?: FilterValues;
  enabled?: boolean;
};

const getEnterpriseServiceAccountsQueryOptions = ({
  enterpriseOrgId,
  pageLimit = 50,
  sortField,
  sortOrder,
  filters,
  enabled = true,
}: EnterpriseServiceAccountsQueryParams) =>
  infiniteQueryOptions({
    queryKey: getEnterpriseServiceAccountsQueryKey({
      enterpriseOrgId,
      pageLimit,
      sortField,
      sortOrder,
      filters,
    }),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.enterprises.listServiceAccounts(
        {
          enterpriseId: enterpriseOrgId,
          pageLimit: pageLimit.toString(),
          pageCursor: pageParam,
          organizationId: filters?.organizationId,
          name: filters?.name,
          sortField,
          sortOrder,
        },
        {
          headers: overrideOrganizationHeader(enterpriseOrgId),
        }
      );
      const mappedServiceAccounts = (res.data.items ?? []).map(
        (sa): ServiceAccountWithGroups => ({
          id: sa.id ?? '',
          name: sa.name ?? '',
          email: sa.email ?? '',
          description: sa.description ?? '',
          createdAt: sa.createTime ?? '',
          managedBy: sa.managedBy ?? '',
          keysCount: sa.keysCount ?? 0,
          organizationId: sa.organizationId ?? '',
          author: {
            id: sa.author?.id ?? '',
            email: sa.author?.email ?? '',
          },
        })
      );

      return {
        ...res.data,
        items: mappedServiceAccounts,
      };
    },
    initialPageParam: '0',
    maxPages: 1,
    getNextPageParam: ({ nextPageCursor }) =>
      nextPageCursor ? nextPageCursor : undefined,
    getPreviousPageParam: ({ prevPageCursor }) =>
      prevPageCursor ? prevPageCursor : undefined,
    enabled: makeIsEnabled(!!enterpriseOrgId, enabled),
  });

type UseEnterpriseServiceAccountsQueryArgs = Omit<
  EnterpriseServiceAccountsQueryParams,
  'enterpriseOrgId'
>;

export const useEnterpriseServiceAccountsQuery = ({
  pageLimit,
  sortField,
  sortOrder,
  filters,
  enabled,
}: UseEnterpriseServiceAccountsQueryArgs) => {
  const { enterpriseOrgId } = useOrganizations();

  return useInfiniteQuery(
    getEnterpriseServiceAccountsQueryOptions({
      enterpriseOrgId: enterpriseOrgId!,
      pageLimit,
      sortField,
      sortOrder,
      filters,
      enabled,
    })
  );
};
